import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  bootstrapApplicationsListOnMount,
  refetchApplicationsListAfterBfcache,
} from '@/lib/applicationsListMountSync';
import type { CompanyApplicationsListScope } from '@/lib/companyApplicationsList';
import { isCompanyApplicationsWarmScope } from '@/lib/companyApplicationsList';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import { shouldWarmPositionHeads, warmPositionApplicationHeads } from '@/lib/warmPositionApplicationHeads';
import type { JobPosition } from '@/types';

/**
 * Sync list caches on Applications mount: session overlay, stale cache wipe,
 * position-head warm, and bfcache recovery (normal F5 vs hard refresh).
 */
export function useApplicationsListMountSync(options: {
  companyId: string | undefined;
  pageReload: boolean;
  loading: boolean;
  positions: JobPosition[];
  listScope: CompanyApplicationsListScope;
}): { listOrderSettling: boolean } {
  const { companyId, pageReload, loading, positions, listScope } = options;
  const queryClient = useQueryClient();
  const listSync = useApplicationListSync();
  const warmRanForMountRef = useRef(0);
  const mountGenerationRef = useRef(0);
  const [listOrderSettling, setListOrderSettling] = useState(false);

  useLayoutEffect(() => {
    mountGenerationRef.current += 1;
    warmRanForMountRef.current = 0;
  }, []);

  useLayoutEffect(() => {
    if (!companyId) return;
    bootstrapApplicationsListOnMount(companyId, queryClient, listSync, { pageReload });
  }, [companyId, pageReload, queryClient, listSync]);

  useEffect(() => {
    if (!shouldWarmPositionHeads(listScope)) {
      setListOrderSettling(false);
      return;
    }
    if (!companyId || loading) {
      setListOrderSettling(true);
      return;
    }
    // Fresh accounts have no positions to warm — don't block the empty state.
    if (positions.length === 0) {
      setListOrderSettling(false);
      return;
    }

    const mountGen = mountGenerationRef.current;
    if (warmRanForMountRef.current === mountGen) return;
    warmRanForMountRef.current = mountGen;
    setListOrderSettling(true);

    void warmPositionApplicationHeads(companyId, positions, (apps) => {
      listSync.patchMany(companyId, apps, false);
    }).finally(() => {
      setListOrderSettling(false);
    });
  }, [companyId, loading, positions, listScope, listSync]);

  useEffect(() => {
    if (!companyId || !isCompanyApplicationsWarmScope(listScope)) return;

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      warmRanForMountRef.current = 0;
      setListOrderSettling(true);
      bootstrapApplicationsListOnMount(companyId, queryClient, listSync, { pageReload: true });
      refetchApplicationsListAfterBfcache(queryClient, companyId);
      if (positions.length === 0 || loading) {
        setListOrderSettling(false);
        return;
      }
      void warmPositionApplicationHeads(companyId, positions, (apps) => {
        listSync.patchMany(companyId, apps, false);
      }).finally(() => setListOrderSettling(false));
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [companyId, listScope, queryClient, listSync, positions, loading]);

  return { listOrderSettling };
}
